const amount = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

export const buildPaymentEmailTargets = (paymentGroups, resources) => {
  const groupedResourceIds = new Set(
    (paymentGroups || []).flatMap((paymentGroup) =>
      paymentGroup.resources.map((resource) => resource.id))
  );
  return [
    ...(paymentGroups || [])
      .filter((paymentGroup) => paymentGroup.resources.length > 0)
      .map((paymentGroup) => ({
        key: `group:${paymentGroup.id}`,
        label: `${paymentGroup.label} (${paymentGroup.resources.length} markets)`,
        group: paymentGroup,
        resources: paymentGroup.resources,
        payoutThreshold: paymentGroup.min_payout_threshold,
        marketOwnerCc: paymentGroup.cc_email || paymentGroup.owner_email || '',
      })),
    ...(resources || [])
      .filter((resource) => !groupedResourceIds.has(resource.id))
      .map((resource) => ({
        key: `resource:${resource.id}`,
        label: `${resource.name} (standalone)`,
        group: null,
        payoutThreshold: resource.min_payout_threshold,
        resources: [{
          id: resource.id,
          name: resource.name,
          is_active: resource.is_active,
        }],
        marketOwnerCc: resource.email || '',
      })),
  ];
};

export const defaultPaymentEmailTarget = (targets, selectedResourceId) =>
  (targets || []).find((target) =>
    target.resources.some((resource) => resource.id === selectedResourceId))
  || targets?.[0]
  || null;

/**
 * Build the group-level participant table used by the payment-email modal.
 *
 * Payment-list responses are already consolidated across tracks/horizons for
 * one resource. This helper performs the remaining aggregation across every
 * member resource: one row per forecaster, summed money columns, and the list
 * of markets covered by their single consolidated email.
 *
 * A sent flag on any member is authoritative. The backend rejects a grouped
 * send containing a forecaster already sent on one sibling, so the UI keeps
 * that participant visible for audit but disables their checkbox.
 */
export const buildGroupParticipants = (paymentsByMember, resources) => {
  const resourceNames = new Map(
    (resources || []).map((resource) => [String(resource.id), resource.name])
  );
  const participants = new Map();

  Object.entries(paymentsByMember || {}).forEach(([resourceId, payments]) => {
    (payments || []).forEach((payment) => {
      if (!payment?.user) return;
      const existing = participants.get(payment.user) || {
        user: payment.user,
        email: payment.email || '',
        invoiceRecipient: payment.invoice_recipient,
        markets: [],
        outstandingPayment: 0,
        monthPayment: 0,
        totalPayment: 0,
        marketTotals: [],
        emailSent: false,
      };

      if (!existing.markets.some((market) => market.id === resourceId)) {
        existing.markets.push({
          id: resourceId,
          name: resourceNames.get(String(resourceId)) || resourceId,
        });
        existing.outstandingPayment += amount(payment.outstanding_payment);
        existing.monthPayment += amount(payment.month_payment);
        existing.totalPayment += amount(payment.total_payment);
        existing.marketTotals.push(amount(payment.total_payment));
      }
      existing.emailSent = existing.emailSent || Boolean(payment.email_sent);
      participants.set(payment.user, existing);
    });
  });

  return Array.from(participants.values())
    .map((participant) => ({
      ...participant,
      markets: participant.markets.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.email.localeCompare(b.email));
};
